import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import authService from '../services/auth.service';
import jwtDecode from "jwt-decode";

export interface AuthState {
  accessToken: string | null,
  refreshToken: string | null,
  userId: string | null,
  userType: string | null
  error: boolean;
}

export interface AuthUser {
  userId: number,
  userType: string
}

const initialState: AuthState = {
  accessToken: JSON.parse(localStorage.getItem('auth')!)?.accessToken,
  refreshToken: JSON.parse(localStorage.getItem('auth')!)?.refreshToken,
  userId: JSON.parse(localStorage.getItem('auth')!)?.userId,
  userType: JSON.parse(localStorage.getItem('auth')!)?.userType,
  error: false
};

export const loginAsync = createAsyncThunk(
  'auth/login',
  async (params: any) => {
    const response = await authService.login(params.username, params.password);
    if (response.status === 200) {
      const userDataResponse = await authService.getUserInfo(params.username);
      return { ...response.data.data, ...userDataResponse.data };
    } else {
      return Promise.reject();
    }
  }
);

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      localStorage.removeItem('auth');
      localStorage.removeItem('isLoginFailed');
      localStorage.removeItem('tokenExpiredAt');
      localStorage.removeItem('username');
      state.accessToken = null;
      state.refreshToken = null;
      state.userId = null;
      state.userType = null;
      state.error = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginAsync.pending, (state) => {
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        const decoded: any = jwtDecode(action.payload.accessToken);

        //add premise and user id object
        let mappedPremiseList: any[] = [];
        action.payload.userInfo?.premises.forEach((item: any) => {
          let myArrayObject = {
            userId: decoded.id,
            premise: item,
          }
          mappedPremiseList.push(myArrayObject);
        });

        action.payload.userInfo?.children.forEach((item: any) => {
          item.premises.forEach((itm: any) => {
            let myArrayObject = {
              userId: item.UserID,
              premise: itm,
            }
            mappedPremiseList.push(myArrayObject);
          });
        });

        if (action.payload.userInfo?.parent) {
          action.payload.userInfo?.parent.premises.forEach((item: any) => {
            let myArrayObject = {
              userId: action.payload.userInfo?.parent.UserID,
              premise: item,
            }
            mappedPremiseList.push(myArrayObject);
          })
        }

        // set up the token expire time in the local storage as 2 hours
        let time = new Date();
        time.setTime(time.getTime() + 7200000);
        localStorage.setItem('tokenExpiredAt', JSON.stringify(time));
        localStorage.setItem('auth', JSON.stringify({
          accessToken: action.payload.accessToken,
          refreshToken: action.payload.refreshToken,
          legalName: action.payload.LegalName,
          callingName: action.payload.CallingName,
          emailAddress: action.payload.EmailAddress,
          imageUrl: action.payload.ImageUrl,
          location: action.payload.userInfo?.premises[0],
          //premises: action.payload.userInfo?.premises,
          userId: decoded.id,
          userType: decoded.type,
          mobileNumber: action.payload.MobileNo,
          isEmailVerified: action.payload.IsEmailValidated,
          isMobileVerified: action.payload.IsMobileValidated,
          countryId: action.payload.userInfo.country.CountryID,
          mappedPremises: mappedPremiseList,
        }));
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.error = false;
      })
      .addCase(loginAsync.rejected, (state) => {
        state.error = true;
      });
  },
});

export const { logout } = authSlice.actions;

export const selectAuth = (state: RootState): string | undefined | null => state.auth.accessToken;

export const selectLoginError = (state: RootState): boolean => state.auth.error;

export const selectUser = (state: RootState): AuthUser => {
  return { userId: Number(state.auth.userId), userType: state.auth.userType! }
};

export const isAuthenticated = (state: RootState): boolean => state.auth.accessToken ? true : false;

export default authSlice.reducer;